import React, { useState, useRef, useEffect } from "react";
import { Form } from "react-bootstrap";
import "./CustomDropdown.css";

const CustomDropdown = ({
  name,
  value,
  onChange,
  options,
  placeholder = "Select...",
  required = false,
  className = "",
  invalid = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const containerRef = useRef(null);

  const selectedOption = options.find(opt => opt.value === value) || null;

  const handleToggle = () => {
    setIsOpen(!isOpen);
  };

  const handleSelect = (option) => {
    onChange({
      target: {
        name,
        value: option.value
      }
    });
    setIsOpen(false);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside);
    
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, []);

  // Adjust dropdown position if it's near viewport edges
  useEffect(() => {
    if (isOpen && dropdownRef.current && containerRef.current) {
      const dropdown = dropdownRef.current;
      const container = containerRef.current;
      
      // Reset position first
      dropdown.style.left = "0";
      dropdown.style.right = "auto";
      dropdown.style.top = "100%";
      dropdown.style.bottom = "auto";
      
      // Get positions
      const containerRect = container.getBoundingClientRect();
      const dropdownRect = dropdown.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      
      // Check if dropdown would overflow right edge
      if (containerRect.left + dropdownRect.width > viewportWidth - 10) {
        dropdown.style.left = "auto";
        dropdown.style.right = "0";
      }
      
      // Check if dropdown would overflow bottom (for mobile)
      if (containerRect.bottom + dropdownRect.height > window.innerHeight - 10) {
        dropdown.style.top = "auto";
        dropdown.style.bottom = "100%";
      }
    }
  }, [isOpen]);

  return (
    <div 
      className={`custom-dropdown-container ${className} ${invalid ? "is-invalid" : ""}`} 
      ref={containerRef}
    >
      <div 
        className={`custom-dropdown-toggle ${isOpen ? 'open' : ''} ${invalid ? 'invalid' : ''}`}
        onClick={handleToggle}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleToggle();
          }
        }}
      >
        <span className="dropdown-selected">
          {selectedOption ? selectedOption.label : placeholder}
        </span>
        <span className="dropdown-arrow">
          {isOpen ? '▲' : '▼'}
        </span>
      </div>
      
      {isOpen && (
        <div 
          className="custom-dropdown-menu"
          ref={dropdownRef}
        >
          {options.map((option) => (
            <div
              key={option.value}
              className={`dropdown-item ${value === option.value ? 'selected' : ''}`}
              onClick={() => handleSelect(option)}
              role="option"
              aria-selected={value === option.value}
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleSelect(option);
                }
              }}
            >
              {option.label}
            </div>
          ))}
        </div>
      )}
      
      {/* Hidden input for form submission */}
      <input
        type="hidden"
        name={name}
        value={value || ""}
        required={required}
      />
    </div>
  );
};

export default CustomDropdown;
